import { useState, useRef, useEffect } from 'react';
import { Bell, ChevronDown, Menu, Search, LogOut, KeyRound, Moon, Sun, Monitor, Languages } from 'lucide-react';
import { useAuth } from '@/store/auth';
import { useTheme } from '@/store/theme';
import { useI18n, useT } from '@/store/i18n';
import { ChangePasswordModal } from '@/components/ChangePasswordModal';
import { cn } from '@/lib/cn';

interface TopbarProps {
  onMenuClick: () => void;
  onSearchClick: () => void;
  onNotificationsClick: () => void;
  unreadCount?: number;
}

function initials(name: string) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

function avatarColor(seed: string) {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash << 5) - hash + seed.charCodeAt(i);
  const palette = [
    'bg-emerald-500', 'bg-blue-500', 'bg-violet-500', 'bg-amber-500',
    'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-pink-500',
  ];
  return palette[Math.abs(hash) % palette.length];
}

export function Topbar({ onMenuClick, onSearchClick, onNotificationsClick, unreadCount = 0 }: TopbarProps) {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const theme = useTheme((s) => s.theme);
  const setTheme = useTheme((s) => s.setTheme);
  const lang = useI18n((s) => s.lang);
  const setLang = useI18n((s) => s.setLang);
  const t = useT();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  function cycleTheme() {
    const next = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
    setTheme(next);
  }

  return (
    <>
      <header className="sticky top-0 z-30 h-16 bg-white/80 dark:bg-slate-900/80 backdrop-blur border-b border-slate-200 dark:border-slate-800">
        <div className="h-full px-4 lg:px-6 flex items-center gap-3">
          <button
            type="button"
            onClick={onMenuClick}
            aria-label={t('topbar.open_menu')}
            className="lg:hidden p-2 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <Menu className="w-5 h-5" />
          </button>

          <button
            type="button"
            onClick={onSearchClick}
            className={cn(
              'flex-1 max-w-md flex items-center gap-2 h-9 px-3 rounded-lg',
              'bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 text-sm',
              'hover:border-slate-300 dark:hover:border-slate-600 hover:bg-white dark:hover:bg-slate-700 transition-colors'
            )}
          >
            <Search className="w-4 h-4" />
            <span className="flex-1 text-left truncate">{t('topbar.search_placeholder')}</span>
            <kbd className="text-[11px] font-mono px-1.5 py-0.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded text-slate-500">
              ⌘K
            </kbd>
          </button>

          {/* Spacer pushes the right-side buttons all the way to the right.
              Was previously lg:hidden which caused everything to bunch left on desktop. */}
          <div className="flex-1" />

          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label={t('topbar.notifications')}
              onClick={onNotificationsClick}
              className="p-2 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 relative"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full flex items-center justify-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="flex items-center gap-2 p-1.5 pr-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <div className={cn('w-8 h-8 rounded-full text-white text-xs font-semibold flex items-center justify-center', avatarColor(user?.username ?? 'u'))}>
                  {initials(user?.full_name ?? user?.username ?? '?')}
                </div>
                <span className="hidden sm:block text-sm font-medium text-slate-700 dark:text-slate-200 max-w-[140px] truncate">
                  {user?.full_name ?? '—'}
                </span>
                <ChevronDown className="w-3 h-3 text-slate-400" />
              </button>

              {menuOpen && (
                <div role="menu" className="absolute right-0 top-full mt-1.5 w-60 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-lg py-1 animate-slide-down">
                  <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-800">
                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                      {user?.full_name}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { cycleTheme(); }}
                    className="w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2"
                  >
                    {theme === 'light' && <Sun className="w-4 h-4 text-amber-500" />}
                    {theme === 'dark' && <Moon className="w-4 h-4 text-blue-400" />}
                    {theme === 'system' && <Monitor className="w-4 h-4 text-slate-500" />}
                    <span className="flex-1">{t('usermenu.theme')}</span>
                    <span className="text-xs text-slate-400">
                      {theme === 'light' ? t('usermenu.theme_light') : theme === 'dark' ? t('usermenu.theme_dark') : t('usermenu.theme_system')}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setLang(lang === 'th' ? 'en' : 'th')}
                    className="w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2"
                  >
                    <Languages className="w-4 h-4 text-slate-400" />
                    <span className="flex-1">{t('usermenu.language')}</span>
                    <span className="text-xs font-mono uppercase text-slate-400">
                      {lang === 'th' ? t('topbar.lang_th') : '🇬🇧 EN'}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); setPwdOpen(true); }}
                    className="w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2"
                  >
                    <KeyRound className="w-4 h-4 text-slate-400" />
                    {t('usermenu.change_password')}
                  </button>
                  <div className="border-t border-slate-100 dark:border-slate-800 mt-1 pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        logout();
                        window.location.href = window.location.pathname.startsWith('/v8') ? '/v8/' : '/';
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      {t('usermenu.logout')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      <ChangePasswordModal open={pwdOpen} onClose={() => setPwdOpen(false)} />
    </>
  );
}
